// Lógica pura de la pestaña «Métricas» (sin React): periodos, días y semanas en hora de Monterrey, y sumas
// por ciudad. La usan la pantalla (src/portal/pantallas/Metricas.tsx), el modo demo y las pruebas
// (pruebas/unidad/metricas.test.ts). Las fechas son AAAA-MM-DD (día de Monterrey), nunca Date con hora.
import { CLAVES_CIUDAD, ciudadMetricaDe, type ClaveCiudad } from "./ciudades-metricas";
import type { Etapa } from "./tipos";

/** Una fila de metricas_plaza_diarias. */
export type MetricaDia = { fecha: string; ciudad: ClaveCiudad; llegaron: number; pasaron: number; descartados: number };
/** Movimientos de etapa de un día (ciudad tal como la escribió el candidato). */
export type MovimientoEtapa = { fecha: string; etapa: Etapa; ciudad: string | null; n: number; aproximado: boolean };

export type MetricasPortal = {
  /** Hoy en Monterrey según la base. */
  hoy: string;
  /** Última sincronización de los conteos (null = todavía no llega ninguna). */
  actualizado_en: string | null;
  dias: MetricaDia[];
  /** Desde cuándo se guarda cada movimiento de etapa (antes: solo el último de cada candidato). */
  registro_etapas_desde: string | null;
  etapas: MovimientoEtapa[];
};

export type Medida = "llegaron" | "pasaron" | "descartados";
export const MEDIDAS: Medida[] = ["llegaron", "pasaron", "descartados"];
export const ETIQUETA_MEDIDA: Record<Medida, string> = {
  llegaron: "Llegaron",
  pasaron: "Pasaron el filtro",
  descartados: "Descartados",
};

export type Periodo = "hoy" | "7" | "15" | "30" | "todo";
export const PERIODOS: [Periodo, string][] = [
  ["hoy", "Hoy"],
  ["7", "Últimos 7 días"],
  ["15", "Últimos 15 días"],
  ["30", "Último mes"],
  ["todo", "Todo el tiempo"],
];
export const esPeriodo = (v: unknown): v is Periodo => PERIODOS.some(([p]) => p === v);

export type Agrupacion = "dia" | "semana";

// ------------------------------------------------------------------ fechas (día de Monterrey)
const DIA_MTY = new Intl.DateTimeFormat("en-CA", { timeZone: "America/Monterrey", year: "numeric", month: "2-digit", day: "2-digit" });

/** Hoy (AAAA-MM-DD) en Monterrey. */
export const hoyMonterrey = (ahora: Date = new Date()): string => DIA_MTY.format(ahora);

const aUTC = (iso: string) => new Date(iso.slice(0, 10) + "T00:00:00Z");
const deUTC = (d: Date) => d.toISOString().slice(0, 10);

export function masDias(iso: string, n: number): string {
  const d = aUTC(iso);
  d.setUTCDate(d.getUTCDate() + n);
  return deUTC(d);
}

/** Lunes de la semana (lunes a domingo) de ese día. */
export function lunesDe(iso: string): string {
  const dow = aUTC(iso).getUTCDay(); // 0 = domingo
  return masDias(iso, dow === 0 ? -6 : 1 - dow);
}

/** Todos los días de desde a hasta, incluidos. */
export function diasEntre(desde: string, hasta: string): string[] {
  const r: string[] = [];
  for (let d = desde; d <= hasta && r.length < 5000; d = masDias(d, 1)) r.push(d);
  return r;
}

export type Rango = { desde: string; hasta: string };

/** El rango del periodo. «Último mes» = los últimos 30 días, hoy incluido. «Todo» arranca en el primer dato. */
export function rangoDe(periodo: Periodo, hoy: string, primera: string | null): Rango {
  if (periodo === "hoy") return { desde: hoy, hasta: hoy };
  if (periodo === "todo") return { desde: primera && primera < hoy ? primera : hoy, hasta: hoy };
  return { desde: masDias(hoy, -(Number(periodo) - 1)), hasta: hoy };
}

/** El mismo largo, justo antes (para comparar «vs. los 7 días anteriores»). null en «todo». */
export function rangoAnterior(periodo: Periodo, r: Rango): Rango | null {
  if (periodo === "todo") return null;
  const largo = diasEntre(r.desde, r.hasta).length;
  return { desde: masDias(r.desde, -largo), hasta: masDias(r.desde, -1) };
}

const MES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const DIA_SEM = ["dom", "lun", "mar", "mié", "jue", "vie", "sáb"];
/** «jue 16 oct» */
export function diaLegible(iso: string): string {
  const d = aUTC(iso);
  return `${DIA_SEM[d.getUTCDay()]} ${d.getUTCDate()} ${MES[d.getUTCMonth()]}`;
}
/** «16 oct» */
export function diaCorto(iso: string): string {
  const d = aUTC(iso);
  return `${d.getUTCDate()} ${MES[d.getUTCMonth()]}`;
}
/** «13 al 19 oct» o «29 sep al 5 oct» */
export function semanaLegible(lunes: string): string {
  const dom = masDias(lunes, 6);
  const a = aUTC(lunes);
  const b = aUTC(dom);
  return a.getUTCMonth() === b.getUTCMonth() ? `${a.getUTCDate()} al ${diaCorto(dom)}` : `${diaCorto(lunes)} al ${diaCorto(dom)}`;
}

// ------------------------------------------------------------------ sumas
export type Conteo = Record<Medida, number>;
const cero = (): Conteo => ({ llegaron: 0, pasaron: 0, descartados: 0 });
const ceroPorCiudad = () => Object.fromEntries(CLAVES_CIUDAD.map((c) => [c, cero()])) as Record<ClaveCiudad, Conteo>;

/** Totales por ciudad dentro del rango (todas las ciudades, aunque sea 0) y el total general. */
export function totalesPorCiudad(dias: MetricaDia[], r: Rango): { porCiudad: Record<ClaveCiudad, Conteo>; total: Conteo } {
  const porCiudad = ceroPorCiudad();
  const total = cero();
  for (const d of dias) {
    if (d.fecha < r.desde || d.fecha > r.hasta) continue;
    const c = porCiudad[d.ciudad] ?? porCiudad.otra;
    for (const m of MEDIDAS) {
      c[m] += d[m];
      total[m] += d[m];
    }
  }
  return { porCiudad, total };
}

/** Un renglón de la tabla o una barra de la gráfica: un día o una semana. */
export type Fila = {
  clave: string; // el día, o el lunes de la semana
  etiqueta: string;
  desde: string;
  hasta: string;
  /** Semana que el periodo corta (no trae sus 7 días). */
  parcial: boolean;
  porCiudad: Record<ClaveCiudad, Conteo>;
  total: Conteo;
};

/**
 * Días (o semanas de lunes a domingo) del rango, del más viejo al más nuevo, con ceros donde no llegó nadie.
 * `ciudad` deja solo esa ciudad (las demás en 0).
 */
export function filasDe(dias: MetricaDia[], r: Rango, agrupacion: Agrupacion, ciudad: ClaveCiudad | null = null): Fila[] {
  const filas = new Map<string, Fila>();
  const llave = (fecha: string) => (agrupacion === "semana" ? lunesDe(fecha) : fecha);
  for (const dia of diasEntre(r.desde, r.hasta)) {
    const k = llave(dia);
    if (filas.has(k)) continue;
    const desde = agrupacion === "semana" ? k : dia;
    const hasta = agrupacion === "semana" ? masDias(k, 6) : dia;
    filas.set(k, {
      clave: k,
      etiqueta: agrupacion === "semana" ? semanaLegible(k) : diaLegible(dia),
      desde,
      hasta,
      parcial: agrupacion === "semana" && (desde < r.desde || hasta > r.hasta),
      porCiudad: ceroPorCiudad(),
      total: cero(),
    });
  }
  for (const d of dias) {
    if (d.fecha < r.desde || d.fecha > r.hasta) continue;
    if (ciudad && d.ciudad !== ciudad) continue;
    const f = filas.get(llave(d.fecha));
    if (!f) continue;
    const c = f.porCiudad[d.ciudad] ?? f.porCiudad.otra;
    for (const m of MEDIDAS) {
      c[m] += d[m];
      f.total[m] += d[m];
    }
  }
  return [...filas.values()];
}

/** Ciudades que tienen algo en el rango (para no pintar columnas en cero). Siempre en el orden de CIUDADES. */
export function ciudadesConDatos(dias: MetricaDia[], r: Rango): ClaveCiudad[] {
  const { porCiudad } = totalesPorCiudad(dias, r);
  return CLAVES_CIUDAD.filter((c) => MEDIDAS.some((m) => porCiudad[c][m] > 0));
}

/** «12 %» de pasaron sobre (pasaron + descartados): de los que el filtro ya decidió, cuántos pasaron. */
export function porcentajePasaron(c: Conteo): string {
  const decididos = c.pasaron + c.descartados;
  return decididos ? `${Math.round((100 * c.pasaron) / decididos)} %` : "—";
}

// ------------------------------------------------------------------ avance de la RH (etapas)
export type EtapaAvance = "contactado" | "entrevistado" | "contratado" | "descartado";
export const ETAPAS_AVANCE: [EtapaAvance, string][] = [
  ["contactado", "Contactados"],
  ["entrevistado", "Entrevistados"],
  ["contratado", "Contratados"],
  ["descartado", "Descartados por ti"],
];

export type FilaAvance = {
  lunes: string;
  etiqueta: string;
  parcial: boolean;
  n: Record<EtapaAvance, number>;
  /** Alguno de esta semana viene de la siembra (solo se sabía su último cambio). */
  aproximado: boolean;
};

/** Movimientos por semana (lunes a domingo), del más nuevo al más viejo. Solo semanas que tocan el rango. */
export function avancePorSemana(etapas: MovimientoEtapa[], r: Rango, ciudad: ClaveCiudad | null = null): FilaAvance[] {
  const semanas = new Map<string, FilaAvance>();
  for (const dia of diasEntre(r.desde, r.hasta)) {
    const l = lunesDe(dia);
    if (!semanas.has(l))
      semanas.set(l, {
        lunes: l,
        etiqueta: semanaLegible(l),
        parcial: l < r.desde || masDias(l, 6) > r.hasta,
        n: { contactado: 0, entrevistado: 0, contratado: 0, descartado: 0 },
        aproximado: false,
      });
  }
  for (const e of etapas) {
    if (e.fecha < r.desde || e.fecha > r.hasta) continue;
    if (!ETAPAS_AVANCE.some(([k]) => k === e.etapa)) continue;
    if (ciudad && ciudadMetricaDe(e.ciudad) !== ciudad) continue;
    const s = semanas.get(lunesDe(e.fecha));
    if (!s) continue;
    s.n[e.etapa as EtapaAvance] += e.n;
    if (e.aproximado) s.aproximado = true;
  }
  return [...semanas.values()].reverse();
}

/** Orden de la tabla: por cualquier columna, asc o desc. */
export type Orden = { col: string; asc: boolean };
export function ordenar<T>(filas: T[], valor: (f: T, col: string) => number | string, o: Orden): T[] {
  return [...filas].sort((a, b) => {
    const x = valor(a, o.col);
    const y = valor(b, o.col);
    const c = x < y ? -1 : x > y ? 1 : 0;
    return o.asc ? c : -c;
  });
}
